import { ReportRounded } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { AdvancedMarker, ColorScheme, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { PropertyMap } from '@/components/admin/PropertyMap';
import { ShareStrip, StatRow, StatTile } from '@/components/admin/StatTile';
import { Switch } from '@/components/common/Switch';
import { AddPropertyModal } from '@/components/dashboard/AddPropertyModal';
import { ScoringStatusChips } from '@/components/dashboard/scoring';
import { Button, CloseButton, Icon, IconButton, Segmented, SortCaret, TextInput } from '@/components/ds';
import { AddressMismatchDialog } from '@/components/uploader/AddressMismatchDialog';
import { LISTING_STATUS_COLOR } from '@/constants/listingStatus';
import { useSnackbar } from '@/context/SnackbarProvider';
import {
  ADMIN_PROPERTIES_QUERY_KEY,
  useAdminProperties,
  useAdminPropertiesSearch,
  useDeleteProperty,
  useDeleteReport,
  useTransferProperty,
  useTriggerRecalculation,
  useUpdateHasHOA,
} from '@/hooks/admin/useAdminProperties';
import { CLIENT_GROUPS_QUERY_KEY, useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { DocumentClassificationType, DOCUMENT_TYPE_LABELS } from '@/models/DocumentRecord';
import { isApiError } from '@/services/api';
import {
  AdminCompletedHoaDocument,
  AdminCompletedNhdDocument,
  AdminIncompleteDocument,
  AdminInspectionReport,
  AdminPendingDocument,
  AdminProcessingDocument,
  AdminProperty,
} from '@/services/api/admin';
import { createListing, deleteListing } from '@/services/api/clientGroups';
import {
  deleteDocumentRecord,
  generateDocumentRecordUrl,
} from '@/services/api/document-records/api';
import {
  cancelPendingUpload,
  confirmAddressOverride,
  createDocuments,
  reportDocumentIssue,
  retryDocumentProcessing,
} from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';
import { downloadFileOnDisk } from '@/utils/files';

const NEIGHBORHOOD_MAP_ID = 'properties-tab-neighborhood-map';

// ─── Design tokens (shared with UsersTab) ──────────────────────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: 'var(--tx-fill)',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
};
const L = {
  critical: '#DB5282',
  urgent: '#FF9A4D',
  moderate: '#FFF981',
  low: '#5AE9FF',
  monitor: '#9A938A',
  resolved: '#C0FF02',
};
const font = "'Manrope', sans-serif";
// Number of manual retries (see DocumentRecord.retryAttemptCount) after which
// the Incomplete row escalates past a plain "Retry" button to also offering
// "Report Issue" (files Sentry Feedback) — see POST /documents/{id}/report-issue.
const RETRY_ESCALATION_THRESHOLD = 2;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function relativeTime(ts: number | undefined): string {
  if (!ts) return '—';
  const ms = Date.now() - ts * 1000;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function initials(name: string): string {
  return name
    .trim()
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function Checkbox({
  checked,
  indeterminate,
  onChange,
  disabled,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onChange();
      }}
      style={{
        width: 13,
        height: 13,
        borderRadius: 3,
        cursor: disabled ? 'default' : 'pointer',
        border: `1.5px solid ${checked || indeterminate ? C.muted : C.subtle}`,
        background: checked || indeterminate ? C.muted : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s',
        opacity: disabled ? 0.4 : 1,
      }}>
      {checked && (
        <svg
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#262626"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
      {indeterminate && !checked && (
        <svg
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#262626"
          strokeWidth="3.5"
          strokeLinecap="round">
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      )}
    </div>
  );
}

// The document count in ink, then a hairline tick per document (up to five), so a home's paperwork reads at a glance
function ReportBadge({ count }: { count: number }) {
  if (count === 0) return <span style={{ fontSize: 13, color: C.subtle, fontFamily: font }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={`${count} document${count === 1 ? '' : 's'}`}>
      <span style={{ fontSize: 13, fontWeight: 500, fontFamily: font, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </span>
      <span aria-hidden style={{ display: 'flex', gap: 2 }}>
        {Array.from({ length: Math.min(count, 5) }, (_, i) => (
          <span key={i} style={{ width: 2, height: 10, borderRadius: 1, background: C.muted }} />
        ))}
      </span>
    </div>
  );
}


/** Renders an avatar + name cell for a person-shaped object (owner or creator).
 *  Falls back to a labeled italic placeholder when the value is null. */
function PersonCell({
  person,
  emptyLabel,
}: {
  person: AdminProperty['owner'];
  emptyLabel: string;
}) {
  if (!person) {
    return (
      <span style={{ fontSize: 12, color: C.subtle, fontFamily: font, fontStyle: 'italic' }}>
        {emptyLabel}
      </span>
    );
  }
  const name = person.name || person.email || person.userId.slice(0, 8);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: C.muted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--tx-text)',
          fontFamily: font,
          flexShrink: 0,
        }}>
        {initials(name)}
      </div>
      <span
        style={{
          fontSize: 12,
          color: C.text,
          fontFamily: font,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {name}
      </span>
    </div>
  );
}

// ─── Transfer Ownership Modal ────────────────────────────────────────────────
function TransferModal({
  property,
  onClose,
  onTransfer,
  isPending,
}: {
  property: AdminProperty;
  onClose: () => void;
  onTransfer: (email: string) => void;
  isPending: boolean;
}) {
  const [email, setEmail] = useState('');

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}>
      <div
        style={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          padding: 28,
          width: 420,
          maxWidth: '90vw',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 20,
          }}>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: C.text,
                fontFamily: font,
                marginBottom: 4,
              }}>
              Transfer ownership
            </div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
              {property.address} · {property.city}, {property.state}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isPending}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: C.subtle,
              padding: 4,
            }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: C.muted,
            fontFamily: font,
            marginBottom: 8,
          }}>
          New Owner Email
        </div>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          disabled={isPending}
          style={{
            width: '100%',
            height: 38,
            background: 'var(--tx-fill)',
            border: 0,
            borderRadius: 10,
            padding: '0 14px',
            color: C.text,
            fontSize: 13,
            fontFamily: font,
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = C.muted;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = C.border;
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
          <button
            onClick={onClose}
            disabled={isPending}
            style={{
              background: 'var(--tx-fill)',
              color: C.muted,
              border: 0,
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: isPending ? 'not-allowed' : 'pointer',
            }}>
            Cancel
          </button>
          <button
            onClick={() => onTransfer(email)}
            disabled={!email.trim() || isPending}
            style={{
              background: C.muted,
              color: 'var(--tx-text)',
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: !email.trim() || isPending ? 'not-allowed' : 'pointer',
              opacity: !email.trim() || isPending ? 0.5 : 1,
            }}>
            {isPending ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Delete Confirmation Modal ───────────────────────────────────────────────
function DeleteModal({
  property,
  onClose,
  onDelete,
  isPending,
}: {
  property: AdminProperty;
  onClose: () => void;
  onDelete: () => void;
  isPending: boolean;
}) {
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}>
      <div
        style={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          padding: 28,
          width: 420,
          maxWidth: '90vw',
        }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: C.text,
            fontFamily: font,
            marginBottom: 4,
          }}>
          Delete property
        </div>
        <div style={{ fontSize: 12, color: C.subtle, fontFamily: font, marginBottom: 16 }}>
          Are you sure you want to delete this property?
        </div>

        <div
          style={{
            background: 'rgba(255,77,77,0.06)',
            border: 0,
            borderRadius: '12px',
            padding: 16,
            marginBottom: 16,
          }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: font }}>
            {property.address}
          </div>
          <div style={{ fontSize: 12, color: C.subtle, fontFamily: font, marginTop: 2 }}>
            {property.city}, {property.state} {property.zipCode}
          </div>
          {property.owner && (
            <div style={{ fontSize: 11, color: C.subtle, fontFamily: font, marginTop: 8 }}>
              Owner: {property.owner.email || property.owner.userId}
            </div>
          )}
        </div>

        <div
          style={{
            background: 'rgba(255,77,77,0.06)',
            border: 0,
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
          <ReportRounded style={{ width: 48, height: 48, flexShrink: 0, color: C.danger }} />
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: C.danger,
                fontFamily: font,
                marginBottom: 4,
              }}>
              Permanent. Cannot be undone.
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-text)', fontFamily: font, lineHeight: 1.5 }}>
              Removes this property from all client groups, deals, and associated data.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            disabled={isPending}
            style={{
              background: 'var(--tx-fill)',
              color: C.muted,
              border: 0,
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: isPending ? 'not-allowed' : 'pointer',
            }}>
            Cancel
          </button>
          <button
            onClick={onDelete}
            disabled={isPending}
            style={{
              background: 'rgba(255,77,77,0.15)',
              color: C.danger,
              border: 0,
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.5 : 1,
            }}>
            {isPending ? 'Deleting…' : 'Delete Property'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Map popup (neighborhood map with POIs) ─────────────────────────────────
// Full bleed on the frame: the map takes the whole left of the screen (landscape laptops and 27" displays), the
// address and nearby places sit in a column on the right
function MapPopup({ property, onClose }: { property: AdminProperty; onClose: () => void }) {
  const { latitude, longitude } = property;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!latitude || !longitude) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(4px)',
        padding: 20,
        boxSizing: 'border-box',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div
        role="dialog"
        aria-label={`Map of ${property.address}`}
        style={{
          height: '100%',
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: 24,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}>
        <NeighborhoodMap
          center={{ latitude, longitude }}
          propertyAddress={property.address}
          subtitle={`${property.city}, ${property.state} ${property.zipCode}`}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body,
  );
}

// ─── Inline neighborhood map (POIs + distance rings) ────────────────────────
const POI_COLORS: Record<string, string> = {
  school: '#0099cc',
  medical: '#4a9e3f',
  fire: '#d4772c',
  police: '#b03060',
};

interface POI {
  label: string;
  type: 'school' | 'medical' | 'fire' | 'police';
  lat: number;
  lng: number;
  distance: string;
}

// Real places around the home, from Google Places (nearby search), the five nearest of each kind within ~2 mi. One
// lookup per kind per home per session: the results are kept here so reopening the map doesn't bill again
const POI_SEARCH: Record<POI['type'], string[]> = {
  school: ['primary_school', 'secondary_school', 'school'],
  medical: ['hospital'],
  fire: ['fire_station'],
  police: ['police'],
};
const nearbyCache = new globalThis.Map<string, POI[]>();

const milesBetween = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(h));
};

function useNearbyPlaces(center: { latitude: number; longitude: number }) {
  const placesLib = useMapsLibrary('places');
  const key = `${center.latitude.toFixed(5)},${center.longitude.toFixed(5)}`;
  const [pois, setPois] = useState<POI[] | null>(() => nearbyCache.get(key) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!placesLib || nearbyCache.has(key)) return;
    let cancelled = false;
    const origin = { lat: center.latitude, lng: center.longitude };
    Promise.all(
      (Object.keys(POI_SEARCH) as POI['type'][]).map(async (type) => {
        const { places } = await placesLib.Place.searchNearby({
          fields: ['displayName', 'location'],
          locationRestriction: { center: origin, radius: 3200 },
          includedPrimaryTypes: POI_SEARCH[type],
          maxResultCount: 5,
          rankPreference: placesLib.SearchNearbyRankPreference.DISTANCE,
        });
        return places.flatMap((pl) => {
          if (!pl.location) return [];
          const at = { lat: pl.location.lat(), lng: pl.location.lng() };
          return [{ label: pl.displayName ?? 'Unnamed', type, ...at, distance: `${milesBetween(origin, at).toFixed(2)}mi` }];
        });
      }),
    )
      .then((groups) => {
        const found = groups.flat();
        nearbyCache.set(key, found);
        if (!cancelled) setPois(found);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [placesLib, key, center.latitude, center.longitude]);

  return { pois: pois ?? [], loading: !pois && !failed, failed };
}

function NeighborhoodRings({ center }: { center: { latitude: number; longitude: number } }) {
  const map = useMap(NEIGHBORHOOD_MAP_ID);
  const mapsLib = useMapsLibrary('maps');

  useEffect(() => {
    if (!map || !mapsLib) return;
    const circles = [804, 1609].map(
      (radius) =>
        new mapsLib.Circle({
          map,
          center: { lat: center.latitude, lng: center.longitude },
          radius,
          strokeColor: 'rgba(200,195,188,0.7)',
          strokeOpacity: 0.4,
          strokeWeight: 1,
          fillOpacity: 0,
        }),
    );
    return () => circles.forEach((c) => c.setMap(null));
  }, [map, mapsLib, center.latitude, center.longitude]);

  return null;
}

const POI_LABELS: Record<POI['type'], string> = {
  school: 'Schools',
  medical: 'Medical',
  fire: 'Fire',
  police: 'Police',
};

function NeighborhoodMap({
  center,
  propertyAddress,
  subtitle,
  onClose,
}: {
  center: { latitude: number; longitude: number };
  propertyAddress: string;
  subtitle: string;
  onClose: () => void;
}) {
  const map = useMap(NEIGHBORHOOD_MAP_ID);
  const { pois, loading, failed } = useNearbyPlaces(center);
  const byDistance = [...pois].sort((x, y) => parseFloat(x.distance) - parseFloat(y.distance));
  const counts = pois.reduce((acc, p) => {
    acc[p.type] = (acc[p.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* the map, edge to edge on the left */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <Map
              id={NEIGHBORHOOD_MAP_ID}
              mapId={GOOGLE_MAPS_MAP_ID}
              defaultCenter={{ lat: center.latitude, lng: center.longitude }}
              defaultZoom={13.5}
              style={{ width: '100%', height: '100%' }}
              // Night follows the app into the map style's dark scheme, as the neighborhood card does
              colorScheme={document.documentElement.dataset.mode === 'night' ? ColorScheme.DARK : ColorScheme.LIGHT}
              gestureHandling="greedy"
              // no on-map controls (Tradehaus's map shows none); Google's logo and attribution stay, as its terms require
              disableDefaultUI>
              <NeighborhoodRings center={center} />
              {pois.map((poi, i) => (
                <AdvancedMarker key={i} position={{ lat: poi.lat, lng: poi.lng }}>
                  <div style={{ position: 'relative', width: 22, height: 22 }}>
                    <div
                      style={{
                        position: 'absolute',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: POI_COLORS[poi.type],
                        opacity: 0.15,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: POI_COLORS[poi.type],
                        top: 5,
                        left: 5,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    />
                  </div>
                </AdvancedMarker>
              ))}
              <AdvancedMarker position={{ lat: center.latitude, lng: center.longitude }}>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--tx-glass-panel)',
                    backgroundImage: 'var(--tx-surface-light)',
                    border: '3px solid var(--tx-line)',
                    boxShadow: 'var(--tx-shadow)',
                  }}
                />
              </AdvancedMarker>
              <AdvancedMarker position={{ lat: center.latitude, lng: center.longitude }}>
                <div
                  style={{
                    background: 'rgba(var(--tx-ink-rgb), 0.05)',
                    borderRadius: '12px',
                    padding: '6px 14px',
                    marginBottom: 26,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    whiteSpace: 'nowrap',
                  }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-text)' }}>
                    {propertyAddress}
                  </span>
                </div>
              </AdvancedMarker>
            </Map>
      </div>

      {/* the place and what's around it */}
      <aside
        style={{
          width: 340,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 20,
          gap: 20,
          boxSizing: 'border-box',
          fontFamily: font,
          overflow: 'auto',
          scrollbarWidth: 'none',
        }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 400, letterSpacing: '-0.02em', color: C.text }}>
              {propertyAddress}
            </div>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginTop: 4 }}>{subtitle}</div>
          </div>
          <CloseButton onClose={onClose} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
          {(Object.keys(POI_LABELS) as POI['type'][]).map((type) => (
            <span
              key={type}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: C.muted }}>
              <span style={{ width: 2, height: 12, borderRadius: 1, background: POI_COLORS[type] }} />
              {POI_LABELS[type]}
              <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {counts[type] ?? 0}
              </span>
            </span>
          ))}
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.subtle }}>Nearby</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.subtle }}>Google Places</span>
          </div>
          {(loading || failed || pois.length === 0) && (
            <div style={{ fontSize: 13, fontWeight: 500, color: C.muted, padding: '10px 0' }}>
              {loading ? 'Finding nearby places…' : failed ? 'Couldn’t load nearby places' : 'Nothing within 2 mi'}
            </div>
          )}
          {byDistance.map((poi, i) => (
            <button
              key={i}
              type="button"
              onClick={() => map?.panTo({ lat: poi.lat, lng: poi.lng })}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 40,
                padding: '0 8px',
                margin: '0 -8px',
                boxSizing: 'content-box',
                border: 0,
                borderRadius: 10,
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: font,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tx-fill)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              <span style={{ width: 2, height: 14, borderRadius: 1, background: POI_COLORS[poi.type], flexShrink: 0 }} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 500,
                  color: C.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {poi.label}
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                {poi.distance}
              </span>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'auto', fontSize: 11, fontWeight: 500, color: C.subtle }}>
          Rings at ½ mi and 1 mi
        </div>
      </aside>
    </div>
  );
}

// ─── Expanded row: property details + access management ──────────────────────
function ExpandedRow({
  property,
  onUpload,
  onMap,
}: {
  property: AdminProperty;
  onUpload: (p: AdminProperty) => void;
  onMap: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { isAdmin } = useUserRole();
  const recalcMutation = useTriggerRecalculation();
  const updateHasHOAMutation = useUpdateHasHOA();

  // one fact: its name, then its value beside it
  const infoRow = (label: string, value: string | React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 34, minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, fontFamily: font, width: 96, flexShrink: 0 }}>
        {label}
      </span>
      {typeof value === 'string' ? (
        <span
          title={value || undefined}
          style={{
            minWidth: 0,
            fontSize: 13,
            fontWeight: 500,
            color: C.text,
            fontFamily: font,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {value || '—'}
        </span>
      ) : (
        value
      )}
    </div>
  );

  const actionBtn = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    opts?: { quiet?: boolean },
  ) => (
    <Button
      size="sm"
      variant={opts?.quiet ? 'ghost' : 'secondary'}
      icon={icon}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}>
      {label}
    </Button>
  );

  return (
    <div
      style={{
        // sits in the open row's box (see the row wrapper), starting under the address column (12 + 28 + 20 + 28 + 20)
        padding: '10px 12px 20px 108px',
      }}>
      {/* ─── Status, then this home's actions (transfer and delete live in the row's ⋮ menu) ─── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <ScoringStatusChips propertyId={property.id} hauserEq={property.hauserEq} />
        <div style={{ flex: 1 }} />
        {property.latitude &&
          property.longitude &&
          actionBtn(
            'Map',
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>,
            onMap,
          )}
        {actionBtn(
          'Upload',
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>,
          () => onUpload(property),
        )}
        {isAdmin &&
          actionBtn(
            recalcMutation.isPending && recalcMutation.variables?.scoreType === 'home'
              ? 'Rescoring Home…'
              : 'Rescore Home',
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>,
            () =>
              !recalcMutation.isPending &&
              recalcMutation.mutate({ propertyId: property.id, scoreType: 'home' }),
            { quiet: true },
          )}
        {isAdmin &&
          actionBtn(
            recalcMutation.isPending && recalcMutation.variables?.scoreType === 'area'
              ? 'Rescoring Area…'
              : 'Rescore Area',
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
              <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
            </svg>,
            () =>
              !recalcMutation.isPending &&
              recalcMutation.mutate({ propertyId: property.id, scoreType: 'area' }),
            { quiet: true },
          )}
      </div>

      {/* ─── Access + Documents ──────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 60,
          alignItems: 'start',
        }}>
        <ClientAssignSection property={property} />
        <DocumentsSection property={property} isAdmin={isAdmin} />
      </div>
      {/* ─── Property details: reference, folded away ─────── */}
      <div style={{ marginTop: 20, paddingTop: 10, borderTop: '1px solid var(--tx-line)' }}>
        <div
          onClick={() => setDetailsOpen((o) => !o)}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.subtle,
            fontFamily: font,
            minHeight: 34,
            marginBottom: detailsOpen ? 4 : 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            userSelect: 'none',
          }}>
          Property details
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            style={{
              transform: detailsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
              flexShrink: 0,
            }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
        {detailsOpen && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 60 }}>
            {infoRow(
              'Owner',
              property.owner
                ? property.owner.name || property.owner.email || property.owner.userId
                : 'Unassigned',
            )}
            {infoRow(
              'HOA',
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Switch
                  checked={!!property.hasHOA}
                  disabled={updateHasHOAMutation.isPending}
                  onChange={(_, checked) =>
                    updateHasHOAMutation.mutate({ propertyId: property.id, hasHOA: checked })
                  }
                />
              </div>,
            )}
            {infoRow(
              'Documents',
              `${property.inspectionReportCount} document${
                property.inspectionReportCount !== 1 ? 's' : ''
              }`,
            )}
            {infoRow('Created', fmtDate(property.createdAt))}
            {infoRow('Updated', fmtDate(property.updatedAt))}
            {property.latitude &&
              property.longitude &&
              infoRow(
                'Coords',
                `${property.latitude.toFixed(4)}, ${property.longitude.toFixed(4)}`,
              )}
            {property.name && infoRow('Name', property.name)}
            {infoRow(
              'ID',
              <span
                title={property.id}
                style={{
                  minWidth: 0,
                  fontSize: 12,
                  color: C.muted,
                  fontFamily: font,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {property.id}
              </span>,
            )}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── Error boundary for sections ─────────────────────────────────────────────
class _SectionErrorBoundary extends React.Component<
  { label: string; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { label: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError)
      return (
        <div
          style={{
            fontSize: 12,
            color: C.subtle,
            fontFamily: font,
            fontStyle: 'italic',
            padding: '4px 0',
          }}>
          Unable to load {this.props.label}
        </div>
      );
    return this.props.children;
  }
}

// ─── Download icon button ────────────────────────────────────────────────────
// Fetches a presigned URL and forces an actual file download (not a browser
// preview — that's what clicking the document chip itself does now). Disabled
// when documentRecordId is missing (e.g. legacy reports we couldn't reverse-
// match) so the user gets a tooltip instead of a broken click.
function DownloadDocButton({
  documentRecordId,
  filename,
  showAlert,
}: {
  documentRecordId?: string;
  filename: string;
  showAlert: (msg: string, severity: 'success' | 'error' | 'warning') => void;
}) {
  const [loading, setLoading] = useState(false);
  if (!documentRecordId) return null;
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const url = await generateDocumentRecordUrl({ id: documentRecordId });
      if (typeof url === 'string' && url.length > 0) {
        await downloadFileOnDisk(url, filename);
      } else {
        showAlert("Couldn't generate a download link.", 'error');
      }
    } catch (err) {
      console.error('Download failed:', err);
      showAlert("Couldn't load the document.", 'error');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      aria-label="Download document"
      title="Download original document"
      disabled={loading}
      onClick={handleClick}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: loading ? 'wait' : 'pointer',
        color: C.subtle,
        padding: '2px 4px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 4,
      }}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
  );
}

// ─── Documents section ───────────────────────────────────────────────────────
function DocumentsSection({ property, isAdmin }: { property: AdminProperty; isAdmin: boolean }) {
  const [reportToDelete, setReportToDelete] = useState<AdminInspectionReport | null>(null);
  const [completedDocumentToDelete, setCompletedDocumentToDelete] = useState<
    AdminCompletedHoaDocument | AdminCompletedNhdDocument | AdminProcessingDocument | null
  >(null);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [retryTarget, setRetryTarget] = useState<AdminPendingDocument | null>(null);
  const [retryError, setRetryError] = useState<string | undefined>(undefined);
  const deleteReportMutation = useDeleteReport();
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();

  // Match advisor mode: only records backed by an uploaded Document belong in
  // the user-visible list. This excludes legacy/orphan report rows and keeps
  // the expanded list consistent with the summary badge.
  const reports = (property.inspectionReports ?? []).filter((report) => report.documentId);
  const pendingDocs = (property.pendingDocuments ?? []).filter((document) => document.documentId);
  const incompleteDocs = (property.incompleteDocuments ?? []).filter(
    (document) => document.documentId,
  );
  const processingDocs = (property.processingDocuments ?? []).filter(
    (document) => document.documentId,
  );
  const completedNhdDocs = (property.completedNhdDocuments ?? []).filter(
    (document) => document.documentId,
  );
  const completedHoaDocs = (property.completedHoaDocuments ?? []).filter(
    (document) => document.documentId,
  );

  const handleOpenRetry = (pending: AdminPendingDocument) => {
    setRetryError(undefined);
    setRetryTarget(pending);
  };

  const handleConfirmRetry = async () => {
    if (!retryTarget?.documentId) {
      setRetryError('This record is not linked to a document, so it cannot be re-run.');
      return;
    }
    setPendingActionId(retryTarget.id);
    try {
      await confirmAddressOverride(retryTarget.documentId);
      showAlert(
        'Re-running with override — the report will appear once processing completes.',
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      setRetryTarget(null);
    } catch (err) {
      console.error('Retry pending upload failed:', err);
      setRetryError(err instanceof Error ? err.message : 'Retry failed.');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDeletePending = async (pending: AdminPendingDocument) => {
    setPendingActionId(pending.id);
    try {
      // Pending uploads are NEEDS_CONFIRMATION mismatches that came in by
      // mistake — delete the whole document (S3 + Document + every record),
      // not just the per-property row. Fall back to a per-record delete for
      // legacy rows missing documentId.
      if (pending.documentId) {
        await cancelPendingUpload(pending.documentId);
      } else {
        await deleteDocumentRecord({ id: pending.id });
      }
      showAlert('Pending upload deleted.', 'success');
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
    } catch (err) {
      console.error('Delete pending upload failed:', err);
      // 409 = another reviewer already resolved this row. Treat as success +
      // refresh, since the user's intent (clear it from the list) is met.
      if (isApiError(err) && err.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
        showAlert('This upload was already resolved by another reviewer.', 'info');
      } else {
        showAlert(
          err instanceof Error ? `Delete failed: ${err.message}` : 'Delete failed.',
          'error',
        );
      }
    } finally {
      setPendingActionId(null);
    }
  };

  const handleRetryIncomplete = async (incomplete: AdminIncompleteDocument) => {
    if (!incomplete.documentId) {
      showAlert('This record is not linked to a document, so it cannot be re-run.', 'error');
      return;
    }
    setPendingActionId(incomplete.id);
    try {
      await retryDocumentProcessing(incomplete.documentId);
      showAlert('Retry started — the report will appear once processing completes.', 'success');
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
    } catch (err) {
      console.error('Retry incomplete upload failed:', err);
      showAlert(err instanceof Error ? `Retry failed: ${err.message}` : 'Retry failed.', 'error');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleReportIncomplete = async (incomplete: AdminIncompleteDocument) => {
    if (!incomplete.documentId) {
      showAlert('This record is not linked to a document, so it cannot be reported.', 'error');
      return;
    }
    setPendingActionId(incomplete.id);
    try {
      await reportDocumentIssue(incomplete.documentId);
      showAlert('Reported to the team.', 'success');
    } catch (err) {
      console.error('Report incomplete upload failed:', err);
      showAlert(err instanceof Error ? `Report failed: ${err.message}` : 'Report failed.', 'error');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDeleteCompletedDocument = async () => {
    if (!completedDocumentToDelete) return;
    setPendingActionId(completedDocumentToDelete.id);
    try {
      await deleteDocumentRecord({ id: completedDocumentToDelete.id });
      showAlert('Document deleted.', 'success');
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
      setCompletedDocumentToDelete(null);
    } catch (err) {
      console.error('Delete completed document failed:', err);
      showAlert(err instanceof Error ? `Delete failed: ${err.message}` : 'Delete failed.', 'error');
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.muted,
          fontFamily: font,
          marginBottom: 16,
          minHeight: 22,
          lineHeight: '22px',
        }}>
        Documents (
        {reports.length +
          pendingDocs.length +
          incompleteDocs.length +
          processingDocs.length +
          completedNhdDocs.length +
          completedHoaDocs.length}
        )
      </div>

      {reports.length === 0 &&
      pendingDocs.length === 0 &&
      incompleteDocs.length === 0 &&
      processingDocs.length === 0 &&
      completedNhdDocs.length === 0 &&
      completedHoaDocs.length === 0 ? (
        <div style={{ fontSize: 12, color: C.subtle, fontFamily: font, fontStyle: 'italic' }}>
          No documents uploaded
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {reports.map((report) => {
            const documentRecordId = report.documentRecordId;
            const typeLabel = report.documentType
              ? DOCUMENT_TYPE_LABELS[report.documentType as DocumentClassificationType] ??
                report.documentType
              : 'Report';
            const dateLabel = report.dateOfInspection
              ? new Date(report.dateOfInspection).toLocaleDateString()
              : report.createdAt
              ? new Date(report.createdAt * 1000).toLocaleDateString()
              : null;
            return (
              <div key={report.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  onClick={
                    documentRecordId
                      ? async (e) => {
                          e.stopPropagation();
                          try {
                            const url = await generateDocumentRecordUrl({
                              id: documentRecordId,
                            });
                            if (typeof url === 'string' && url.length > 0) {
                              window.open(url, '_blank', 'noopener,noreferrer');
                            } else {
                              showAlert("Couldn't generate a preview link.", 'error');
                            }
                          } catch (err) {
                            console.error('Preview failed:', err);
                            showAlert("Couldn't load the document.", 'error');
                          }
                        }
                      : undefined
                  }
                  title={report.documentRecordId ? 'Preview document' : undefined}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: C.muted,
                    background: 'var(--tx-fill-2)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: report.documentRecordId ? 'pointer' : 'default',
                  }}>
                  {typeLabel}
                  {report.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-muted)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                {dateLabel && (
                  <span style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
                    {dateLabel}
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={report.documentRecordId}
                    filename={`${typeLabel}${dateLabel ? ` - ${dateLabel}` : ''}.pdf`}
                    showAlert={showAlert}
                  />
                  {isAdmin && (
                    <button
                      aria-label="Delete report"
                      title="Delete report"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReportToDelete(report);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: C.subtle,
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        lineHeight: 1,
                        fontSize: 17,
                      }}>
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {pendingDocs.map((pending) => {
            const busy = pendingActionId === pending.id;
            const typeLabel = pending.documentType
              ? DOCUMENT_TYPE_LABELS[pending.documentType as DocumentClassificationType] ??
                pending.documentType
              : 'Report';
            return (
              <div
                key={`pending-${pending.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: busy ? 0.6 : 1 }}>
                <span
                  title={
                    pending.extractedAddress
                      ? `Detected: ${pending.extractedAddress}`
                      : 'Address could not be verified'
                  }
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: '#1a1a1a',
                    background: '#f6c14a',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                  {typeLabel}
                  {pending.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-text)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: '#f6c14a',
                    fontFamily: font,
                    fontStyle: 'italic',
                  }}>
                  Pending approval
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={pending.id}
                    filename={`${typeLabel}.pdf`}
                    showAlert={showAlert}
                  />
                  <button
                    aria-label="Re-run with override"
                    title="Review and re-run with override"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenRetry(pending);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: busy ? 'wait' : 'pointer',
                      color: '#f6c14a',
                      padding: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 4,
                      fontSize: 14,
                    }}>
                    {/* caution / refresh */}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </button>
                  <button
                    aria-label="Delete pending upload"
                    title="Delete this pending upload"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePending(pending);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: busy ? 'wait' : 'pointer',
                      color: C.subtle,
                      padding: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 4,
                      lineHeight: 1,
                      fontSize: 17,
                    }}>
                    ×
                  </button>
                </div>
              </div>
            );
          })}
          {incompleteDocs.map((incomplete) => {
            const busy = pendingActionId === incomplete.id;
            const typeLabel = incomplete.documentType
              ? DOCUMENT_TYPE_LABELS[incomplete.documentType as DocumentClassificationType] ??
                incomplete.documentType
              : 'Report';
            // After a couple of retries have already failed, a third
            // identical retry click is unlikely to be the fix — surface the
            // Report Issue escalation instead of just repeating "try again".
            const retryCount = incomplete.retryAttemptCount ?? 0;
            const isEscalated = retryCount >= RETRY_ESCALATION_THRESHOLD;
            return (
              <div
                key={`incomplete-${incomplete.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: busy ? 0.6 : 1 }}>
                <span
                  title="A Pulse-dependent step exhausted its retry budget — most likely Pulse was unavailable."
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: 'var(--tx-text)',
                    background: L.urgent,
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                  {typeLabel}
                  {incomplete.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-text)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: L.urgent,
                    fontFamily: font,
                    fontStyle: 'italic',
                  }}>
                  {isEscalated
                    ? `Incomplete — failed ${retryCount}x, Pulse still unavailable`
                    : 'Incomplete — Pulse unavailable'}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={incomplete.id}
                    filename={`${typeLabel}.pdf`}
                    showAlert={showAlert}
                  />
                  {isEscalated && (
                    <button
                      aria-label="Report issue to team"
                      title="File a Sentry report with this document's context for the team to investigate"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReportIncomplete(incomplete);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: busy ? 'wait' : 'pointer',
                        color: C.danger,
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        fontSize: 14,
                      }}>
                      {/* flag/report */}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                        <line x1="4" y1="22" x2="4" y2="15" />
                      </svg>
                    </button>
                  )}
                  <button
                    aria-label="Retry processing"
                    title="Retry processing this document"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryIncomplete(incomplete);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: busy ? 'wait' : 'pointer',
                      color: L.urgent,
                      padding: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 4,
                      fontSize: 14,
                    }}>
                    {/* refresh/retry */}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                  </button>
                  <button
                    aria-label="Delete incomplete upload"
                    title="Delete this incomplete upload — consider asking the user to re-upload if this document is permanently stuck"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePending(incomplete);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: busy ? 'wait' : 'pointer',
                      color: C.subtle,
                      padding: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 4,
                      lineHeight: 1,
                      fontSize: 17,
                    }}>
                    ×
                  </button>
                </div>
              </div>
            );
          })}
          {processingDocs.map((processing: AdminProcessingDocument) => {
            const typeLabel = processing.documentType
              ? DOCUMENT_TYPE_LABELS[processing.documentType as DocumentClassificationType] ??
                processing.documentType
              : 'Report';
            return (
              <div
                key={`processing-${processing.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: C.muted,
                    background: 'var(--tx-fill-2)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                  {typeLabel}
                  {processing.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-muted)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: C.subtle,
                    fontFamily: font,
                    fontStyle: 'italic',
                  }}>
                  Processing…
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={processing.id}
                    filename={`${typeLabel}.pdf`}
                    showAlert={showAlert}
                  />
                  {isAdmin && (
                    <button
                      aria-label={`Delete ${typeLabel}`}
                      title="Delete document"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompletedDocumentToDelete(processing);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: C.subtle,
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        lineHeight: 1,
                        fontSize: 17,
                      }}>
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {completedNhdDocs.map((completed: AdminCompletedNhdDocument) => {
            const typeLabel = completed.documentType
              ? DOCUMENT_TYPE_LABELS[completed.documentType as DocumentClassificationType] ??
                completed.documentType
              : 'Report';
            const dateLabel = new Date(completed.completedAt * 1000).toLocaleDateString();
            return (
              <div
                key={`completed-nhd-${completed.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const url = await generateDocumentRecordUrl({ id: completed.id });
                      if (typeof url === 'string' && url.length > 0) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        showAlert("Couldn't generate a preview link.", 'error');
                      }
                    } catch (err) {
                      console.error('Preview failed:', err);
                      showAlert("Couldn't load the document.", 'error');
                    }
                  }}
                  title="Preview document"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: C.muted,
                    background: 'var(--tx-fill-2)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                  }}>
                  {typeLabel}
                  {completed.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-muted)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>{dateLabel}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={completed.id}
                    filename={`${typeLabel}.pdf`}
                    showAlert={showAlert}
                  />
                  {isAdmin && (
                    <button
                      aria-label={`Delete ${typeLabel}`}
                      title="Delete document"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompletedDocumentToDelete(completed);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: C.subtle,
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        lineHeight: 1,
                        fontSize: 17,
                      }}>
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {completedHoaDocs.map((completed: AdminCompletedHoaDocument) => {
            const typeLabel = completed.documentType
              ? DOCUMENT_TYPE_LABELS[completed.documentType as DocumentClassificationType] ??
                completed.documentType
              : 'Report';
            const dateLabel = new Date(completed.completedAt * 1000).toLocaleDateString();
            return (
              <div
                key={`completed-hoa-${completed.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const url = await generateDocumentRecordUrl({ id: completed.id });
                      if (typeof url === 'string' && url.length > 0) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        showAlert("Couldn't generate a preview link.", 'error');
                      }
                    } catch (err) {
                      console.error('Preview failed:', err);
                      showAlert("Couldn't load the document.", 'error');
                    }
                  }}
                  title="Preview document"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    color: C.muted,
                    background: 'var(--tx-fill-2)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                  }}>
                  {typeLabel}
                  {completed.source === 'buyer' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--tx-muted)',
                        border: 0,
                        background: 'var(--tx-fill)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        marginLeft: 2,
                      }}>
                      BUYER
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>{dateLabel}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <DownloadDocButton
                    documentRecordId={completed.id}
                    filename={`${typeLabel}.pdf`}
                    showAlert={showAlert}
                  />
                  {isAdmin && (
                    <button
                      aria-label={`Delete ${typeLabel}`}
                      title="Delete document"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompletedDocumentToDelete(completed);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: C.subtle,
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        lineHeight: 1,
                        fontSize: 17,
                      }}>
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Delete confirmation dialog ──────────────────────── */}
      {reportToDelete &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'var(--tx-backdrop)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={() => {
              if (!deleteReportMutation.isPending) setReportToDelete(null);
            }}>
            <div
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                padding: '28px 28px 24px',
                maxWidth: 420,
                width: '90%',
                fontFamily: font,
              }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 12 }}>
                Delete Inspection Report?
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                This will permanently delete all extracted issues from this report and trigger a
                condition score recalculation.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setReportToDelete(null)}
                  disabled={deleteReportMutation.isPending}
                  style={{
                    background: 'var(--tx-fill)',
                    color: C.text,
                    border: 0,
                    borderRadius: 8,
                    padding: '6px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  disabled={deleteReportMutation.isPending}
                  onClick={() => {
                    deleteReportMutation.mutate(
                      {
                        propertyId: property.id,
                        reportId: reportToDelete.id,
                        documentRecordId: reportToDelete.documentRecordId,
                      },
                      { onSuccess: () => setReportToDelete(null) },
                    );
                  }}
                  style={{
                    background: 'rgba(255,77,77,0.12)',
                    color: C.danger,
                    border: 0,
                    borderRadius: 8,
                    padding: '6px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: deleteReportMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: deleteReportMutation.isPending ? 0.6 : 1,
                    minWidth: 70,
                  }}>
                  {deleteReportMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {completedDocumentToDelete &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'var(--tx-backdrop)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={() => {
              if (pendingActionId !== completedDocumentToDelete.id)
                setCompletedDocumentToDelete(null);
            }}>
            <div
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                padding: '28px 28px 24px',
                maxWidth: 420,
                width: '90%',
                fontFamily: font,
              }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 12 }}>
                Delete Document?
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                This permanently deletes the uploaded file and removes its document chips for every
                user.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setCompletedDocumentToDelete(null)}
                  disabled={pendingActionId === completedDocumentToDelete.id}
                  style={{
                    background: 'var(--tx-fill)',
                    color: C.text,
                    border: 0,
                    borderRadius: 8,
                    padding: '6px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  disabled={pendingActionId === completedDocumentToDelete.id}
                  onClick={handleDeleteCompletedDocument}
                  style={{
                    background: 'rgba(255,77,77,0.12)',
                    color: C.danger,
                    border: 0,
                    borderRadius: 8,
                    padding: '6px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor:
                      pendingActionId === completedDocumentToDelete.id ? 'not-allowed' : 'pointer',
                    opacity: pendingActionId === completedDocumentToDelete.id ? 0.6 : 1,
                    minWidth: 70,
                  }}>
                  {pendingActionId === completedDocumentToDelete.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <AddressMismatchDialog
        open={!!retryTarget}
        payload={
          retryTarget
            ? {
                documentId: retryTarget.documentId ?? '',
                documentRecordId: retryTarget.id,
                documentName: retryTarget.name,
                propertyId: property.id,
                extractedAddress: retryTarget.extractedAddress ?? null,
                propertyAddress: `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`,
                addressConfidence: retryTarget.extractedAddressConfidence ?? 0,
                confidenceThreshold: retryTarget.confidenceThreshold ?? 0.5,
                reason: 'Pending approval — review and confirm to re-run.',
              }
            : undefined
        }
        isLoading={pendingActionId === retryTarget?.id}
        error={retryError}
        onConfirm={handleConfirmRetry}
        onCancel={async () => {
          // Cancel = throw it all away. Pending uploads are almost always the
          // wrong file dropped on the wrong property; the user shouldn't have
          // to chase a separate × button to clean it up.
          const target = retryTarget;
          setRetryTarget(null);
          setRetryError(undefined);
          if (target) await handleDeletePending(target);
        }}
        onDismiss={() => {
          // × / ESC — defer. Leave the pending row alone so another reviewer
          // can resolve it later.
          setRetryTarget(null);
          setRetryError(undefined);
        }}
      />
    </div>
  );
}

// ─── Upload modal ────────────────────────────────────────────────────────────
function UploadModal({
  property,
  onClose,
  onUpload,
}: {
  property: AdminProperty;
  onClose: () => void;
  onUpload: (propertyId: string, file: File) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const hasReport = property.inspectionReportCount > 0;

  const handleSubmit = () => {
    if (!file) return;
    // Close the modal immediately so the user can see the sidebar processing
    // indicator and any downstream UI (e.g. the address-mismatch modal) without
    // this upload modal blocking it. The upload runs in the background;
    // success/error are surfaced via toasts inside onUpload.
    onClose();
    onUpload(property.id, file).catch((err) => {
      console.error('Admin report upload failed:', err);
    });
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div
        style={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          padding: 28,
          width: 420,
          maxWidth: '90vw',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 20,
          }}>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: C.text,
                fontFamily: font,
                marginBottom: 4,
              }}>
              Upload report
            </div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
              {property.address} · {property.city}, {property.state}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: C.subtle,
              padding: 4,
            }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {hasReport && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'var(--tx-fill)',
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 16,
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={L.resolved}
              strokeWidth="2"
              strokeLinecap="round"
              style={{ flexShrink: 0 }}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: font }}>
                {property.inspectionReportCount} document
                {property.inspectionReportCount !== 1 ? 's' : ''} on file
              </div>
            </div>
          </div>
        )}
        <div
          onClick={() => fileRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) setFile(f);
          }}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: `1.5px dashed ${file ? C.muted : C.border}`,
            borderRadius: '12px',
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            background: file ? 'var(--tx-fill)' : 'transparent',
          }}>
          {file ? (
            <>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.muted}
                strokeWidth="2"
                strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: font }}>
                {file.name}
              </div>
              <div style={{ fontSize: 11, color: C.subtle, fontFamily: font }}>
                {(file.size / 1024 / 1024).toFixed(1)} MB · Click to replace
              </div>
            </>
          ) : (
            <>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.subtle}
                strokeWidth="1.5"
                strokeLinecap="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                Click to upload or drag &amp; drop
              </div>
              <div style={{ fontSize: 11, color: C.subtle, fontFamily: font }}>
                PDF, DOCX, JPG, PNG — max 25MB
              </div>
            </>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--tx-fill)',
              color: C.muted,
              border: 0,
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!file}
            style={{
              background: C.muted,
              color: 'var(--tx-text)',
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: file ? 'pointer' : 'not-allowed',
              opacity: file ? 1 : 0.4,
            }}>
            Upload Report
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Assigned deals section (in expanded row) ────────────────────────────────
function ClientAssignSection({ property }: { property: AdminProperty }) {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const queryClient = useQueryClient();
  const { groups: rawGroups } = useClientGroups();
  const [isPending, setIsPending] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [search, setSearch] = useState('');

  // Current access only — Lost/Passed revoke group access on the BE so they
  // should not appear here. They remain in the underlying listings for audit.
  const assignedListings = useMemo(() => {
    const rows: {
      listingId: string;
      listingStatus: string;
      groupName: string;
      dealName: string;
      memberLabel: string;
      memberEmail: string;
    }[] = [];
    for (const g of rawGroups) {
      const groupName = g.holdingName || g.members[0]?.name || g.id;
      const memberName = g.members[0]?.name || '';
      const memberEmail = g.members[0]?.email || '';
      const memberLabel = memberName || memberEmail;
      for (const deal of g.deals ?? []) {
        for (const listing of deal.listings ?? []) {
          if (
            listing.propertyId === property.id &&
            listing.status !== 'Lost' &&
            listing.status !== 'Passed'
          ) {
            rows.push({
              listingId: listing.id,
              listingStatus: listing.status,
              groupName,
              dealName: deal.name,
              memberLabel,
              memberEmail,
            });
          }
        }
      }
    }
    return rows;
  }, [rawGroups, property.id]);

  // Flat list of group › deal options for the one-step search
  const dealOptions = useMemo(() => {
    const opts: { dealId: string; groupName: string; dealName: string; owner: string }[] = [];
    for (const g of rawGroups) {
      if (g.status !== 'active') continue;
      const groupName = g.holdingName || g.members[0]?.name || g.id;
      const owner = g.members[0]?.email || '';
      for (const deal of g.deals ?? []) {
        if (deal.status === 'Closed') continue;
        opts.push({ dealId: deal.id, groupName, dealName: deal.name, owner });
      }
    }
    return opts;
  }, [rawGroups]);

  const matches = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return dealOptions
      .filter(
        (o) =>
          o.groupName.toLowerCase().includes(q) ||
          o.dealName.toLowerCase().includes(q) ||
          o.owner.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [dealOptions, search]);

  const handleAssign = async (dealId: string) => {
    setIsPending(true);
    try {
      const addr = [property.address, property.city].filter(Boolean).join(', ');
      await createListing(dealId, { address: addr, propertyId: property.id, status: 'Touring' });
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      showAlert(t('deal.property_added'), 'success');
    } catch {
      showAlert(t('deal.property_add_failed'), 'error');
    } finally {
      setIsPending(false);
      setIsAssigning(false);
      setSearch('');
    }
  };

  const handleRemove = async (listingId: string) => {
    setIsPending(true);
    try {
      await deleteListing(listingId);
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      showAlert(t('deal.property_removed'), 'success');
    } catch {
      showAlert(t('deal.property_remove_failed'), 'error');
    } finally {
      setIsPending(false);
    }
  };

  const statusColor = (s: string) => LISTING_STATUS_COLOR[s] ?? C.muted;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 12,
          minHeight: 22,
        }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: C.muted,
            fontFamily: font,
            lineHeight: '22px',
          }}>
          Access ({assignedListings.length})
        </div>
        {!isAssigning && (
          <button
            onClick={() => setIsAssigning(true)}
            style={{
              background: 'var(--tx-fill)',
              color: C.text,
              border: 0,
              borderRadius: 8,
              padding: '5px 12px',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: font,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}>
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Grant Access
          </button>
        )}
      </div>

      {/* One-step search: type Group or Deal name → click to assign */}
      {isAssigning && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client or deal name…"
              disabled={isPending}
              style={{
                width: '100%',
                height: 32,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: 8,
                padding: '0 12px',
                color: C.text,
                fontSize: 12,
                fontFamily: font,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                setTimeout(() => {
                  e.target.style.borderColor = C.border;
                }, 150);
              }}
            />
            {matches.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 36,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  background: C.card,
                  borderRadius: '9px',
                  boxShadow: 'var(--tx-shadow-sm)',
                  overflow: 'hidden',
                }}>
                {matches.map((o) => (
                  <div
                    key={o.dealId}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAssign(o.dealId);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 12,
                      fontFamily: font,
                      color: C.text,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: C.muted,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--tx-text)',
                        fontFamily: font,
                        flexShrink: 0,
                      }}>
                      {initials(o.groupName)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {o.groupName}
                        <span style={{ color: C.subtle, fontWeight: 400 }}> › </span>
                        {o.dealName}
                      </div>
                      {o.owner && <div style={{ fontSize: 10, color: C.subtle }}>{o.owner}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setIsAssigning(false);
              setSearch('');
            }}
            disabled={isPending}
            style={{
              background: 'transparent',
              color: C.muted,
              border: 'none',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: font,
              cursor: 'pointer',
              padding: '6px 8px',
            }}>
            Cancel
          </button>
        </div>
      )}

      {/* Assigned listings */}
      {assignedListings.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: C.subtle,
            fontFamily: font,
            fontStyle: 'italic',
            padding: '4px 0',
          }}>
          No clients assigned
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {assignedListings.map((row) => (
            <div
              key={row.listingId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: '#ffffff06',
                borderRadius: 10,
                padding: '9px 12px',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--tx-fill)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  color: C.muted,
                  fontFamily: font,
                  flexShrink: 0,
                }}>
                {initials(row.memberLabel || row.groupName)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    fontFamily: font,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {row.groupName}
                  <span style={{ color: C.subtle, fontWeight: 400 }}> › </span>
                  {row.dealName}
                </div>
                {row.memberLabel && (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.text,
                      opacity: 0.65,
                      fontFamily: font,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {row.memberLabel}
                    {row.memberEmail && row.memberLabel !== row.memberEmail && (
                      <span style={{ opacity: 0.75 }}> - {row.memberEmail}</span>
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: font,
                  color: statusColor(row.listingStatus),
                  background: `${statusColor(row.listingStatus)}18`,
                  border: 0,
                  borderRadius: 6,
                  padding: '3px 8px',
                  flexShrink: 0,
                }}>
                {row.listingStatus}
              </div>
              <button
                onClick={() => handleRemove(row.listingId)}
                disabled={isPending}
                style={{
                  background: 'transparent',
                  color: C.subtle,
                  border: 'none',
                  cursor: isPending ? 'not-allowed' : 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Row context menu (3-dot vertical) ──────────────────────────────────────
function RowMenu({
  onExpand,
  onTransfer,
  onDelete,
}: {
  property: AdminProperty;
  onExpand: () => void;
  onTransfer: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const menuItem = (label: string, onClick: () => void, opts?: { danger?: boolean }) => (
    <div
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
        setOpen(false);
      }}
      style={{
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: font,
        cursor: 'pointer',
        borderRadius: 6,
        color: opts?.danger ? C.danger : C.text,
        background: 'transparent',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}>
      {label}
    </div>
  );

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
          }
          setOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 6,
          background: open ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
          border: 0,
          color: C.subtle,
          cursor: 'pointer',
        }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              zIndex: 9999,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '12px',
              padding: '4px 6px',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              minWidth: 160,
              boxShadow: 'var(--tx-shadow)',
            }}>
            {menuItem('View Details', onExpand)}
            {menuItem('Transfer Ownership', onTransfer)}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 6px' }} />
            {menuItem('Delete Property', onDelete, { danger: true })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── Resizable columns ──────────────────────────────────────────────────────
// Indices: 0=address, 1=city, 2=state, 3=zip, 4=createdBy, 5=updated, 6=docs
const DEFAULT_COL_WIDTHS = [280, 80, 80, 80, 160, 90, 50];
const MIN_COL_WIDTH = 40;

function buildGrid(widths: number[]): string {
  // checkbox | chevron | address | city | state | zip | created by | updated | docs | menu
  // address, city and created by share any spare width (2 : 1 : 1), so a wide screen doesn't pile it all on address
  return `28px 28px minmax(${widths[0]}px,2fr) minmax(${widths[1]}px,1fr) ${widths[2]}px ${widths[3]}px minmax(${widths[4]}px,1fr) ${widths[5]}px ${widths[6]}px 48px`;
}

function ResizeHandle({ onStart }: { onStart: () => (delta: number) => void }) {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const resizer = onStart();
    const onMouseMove = (ev: MouseEvent) => resizer(ev.clientX - startX);
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: 'absolute',
        right: -4,
        top: 0,
        bottom: 0,
        width: 8,
        cursor: 'col-resize',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <div
        style={{
          width: 1,
          height: 16,
          background: 'transparent',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.background = C.subtle;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.background = 'transparent';
        }}
      />
    </div>
  );
}

// ─── Sort ────────────────────────────────────────────────────────────────────
type SortKey = 'address' | 'city' | 'state' | 'zip' | 'owner' | 'docs' | 'updated';
type ViewMode = 'list' | 'map';

// ─── Main component ─────────────────────────────────────────────────────────
export const PropertiesTab = () => {
  // Full auto-fetch — kept only for the "Total"/withReports/mappedCount stat
  // tiles and the map view (both genuinely need the whole table, same
  // rationale as PropertiesPageNew's KpiBar). The actual list rows below use
  // useAdminPropertiesSearch instead — real server-side search + pagination,
  // not a client-side filter over this full array (see #1268).
  const { properties, isLoading, error } = useAdminProperties();
  const deleteMutation = useDeleteProperty();
  const transferMutation = useTransferProperty();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const {
    properties: searchedProperties,
    isLoading: isLoadingSearchedProperties,
    hasNextPage: hasNextPropertiesPage,
    isFetchingNextPage: isFetchingNextPropertiesPage,
    fetchNextPage: fetchNextPropertiesPage,
  } = useAdminPropertiesSearch({ search: debouncedSearchQuery });
  // True while a search result is pending — either the debounce hasn't
  // settled yet or the debounced request is still in flight. Without this,
  // the empty state below would flash "No properties match your search" for
  // every keystroke before the real (possibly non-empty) result lands.
  const isSearchPending = searchQuery.trim() !== debouncedSearchQuery || isLoadingSearchedProperties;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Resizable columns
  const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS);
  const grid = useMemo(() => buildGrid(colWidths), [colWidths]);

  const startResize = (colIndex: number) => {
    const snapshot = [...colWidths];
    const baseLeft = snapshot[colIndex];
    // Find next resizable neighbor (skip if at end)
    const nextIndex = colIndex + 1 < snapshot.length ? colIndex + 1 : -1;
    const baseRight = nextIndex >= 0 ? snapshot[nextIndex] : 0;
    return (delta: number) => {
      const next = [...snapshot];
      const maxDelta = nextIndex >= 0 ? baseRight - MIN_COL_WIDTH : 0;
      const minDelta = -(baseLeft - MIN_COL_WIDTH);
      const clamped = Math.max(minDelta, Math.min(maxDelta, delta));
      next[colIndex] = baseLeft + clamped;
      if (nextIndex >= 0) next[nextIndex] = baseRight - clamped;
      setColWidths(next);
    };
  };

  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  // Modal states
  const [transferTarget, setTransferTarget] = useState<AdminProperty | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminProperty | null>(null);
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [mapTarget, setMapTarget] = useState<AdminProperty | null>(null);
  const [uploadTarget, setUploadTarget] = useState<AdminProperty | null>(null);

  const handleUpload = async (propertyId: string, file: File, source: 'seller' | 'buyer') => {
    try {
      const [presigned] = await generatePreSignedUrls({
        files: [
          {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            isExtractable: true,
          },
        ],
      });
      await fetch(presigned.preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      // propertyId MUST be passed — POST /documents only writes the DocumentRecord
      // (the link the classifier reads to verify the address) when propertyId is
      // present. Omitting it leaves the document unlinked and breaks the entire
      // address-verification flow.
      await createDocuments({
        propertyId,
        source,
        documents: [
          {
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            s3Key: presigned.key,
          },
        ],
      });
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      showAlert(t('report.uploaded'), 'success');
    } catch (err) {
      // The modal closes immediately on click, so failures need to surface as
      // a toast. Prefer the backend's user-facing `message` (e.g. the friendly
      // duplicate_file copy) over the raw axios "Request failed with status
      // code 409".
      console.error('Admin report upload failed:', err);
      let message = 'Upload failed.';
      const maybeAxios = err as { response?: { data?: { message?: string } } } | undefined;
      const backendMessage = maybeAxios?.response?.data?.message;
      if (backendMessage) {
        message = backendMessage;
      } else if (err instanceof Error && err.message) {
        message = `Upload failed: ${err.message}`;
      }
      showAlert(message, 'error');
    }
  };

  // Filter
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return properties;
    const q = searchQuery.toLowerCase();
    return properties.filter(
      (p) =>
        p.address?.toLowerCase().includes(q) ||
        p.city?.toLowerCase().includes(q) ||
        p.zipCode?.toLowerCase().includes(q) ||
        p.name?.toLowerCase().includes(q) ||
        p.owner?.email?.toLowerCase().includes(q),
    );
  }, [properties, searchQuery]);

  // Sort — server already applied the search (searchedProperties), so this
  // only sorts within the currently-loaded page(s), same tradeoff as
  // PropertiesPageNew. `filtered` (client search over the full `properties`)
  // is kept separate, feeding only the map view below, which still wants to
  // show every matching pin rather than one page's worth.
  const sorted = useMemo(() => {
    const arr = [...searchedProperties];
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortKey === 'address') {
        av = (a.address || '').toLowerCase();
        bv = (b.address || '').toLowerCase();
      } else if (sortKey === 'city') {
        av = (a.city || '').toLowerCase();
        bv = (b.city || '').toLowerCase();
      } else if (sortKey === 'state') {
        av = (a.state || '').toLowerCase();
        bv = (b.state || '').toLowerCase();
      } else if (sortKey === 'zip') {
        av = a.zipCode || '';
        bv = b.zipCode || '';
      } else if (sortKey === 'owner') {
        av = (a.owner?.email || 'zzz').toLowerCase();
        bv = (b.owner?.email || 'zzz').toLowerCase();
      } else if (sortKey === 'docs') {
        av = a.inspectionReportCount;
        bv = b.inspectionReportCount;
      } else if (sortKey === 'updated') {
        av = a.updatedAt || 0;
        bv = b.updatedAt || 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [searchedProperties, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        sorted.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        sorted.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = () => {
    const toDelete = properties.filter((p) => selectedIds.has(p.id));
    toDelete.forEach((p) => deleteMutation.mutate(p.id));
    clearSelection();
  };

  const handleTransfer = (email: string) => {
    if (!transferTarget) return;
    transferMutation.mutate(
      { propertyId: transferTarget.id, toUserEmail: email, documentRecordIds: [] },
      { onSuccess: () => setTransferTarget(null) },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => setDeleteTarget(null),
    });
  };

  const allSelected = sorted.length > 0 && sorted.every((p) => selectedIds.has(p.id));
  const someSelected = sorted.some((p) => selectedIds.has(p.id)) && !allSelected;

  // Stats
  const withReports = properties.filter((p) => p.inspectionReportCount > 0).length;
  const withoutReports = properties.filter((p) => p.inspectionReportCount === 0).length;
  const mappedCount = properties.filter((p) => p.latitude != null && p.longitude != null).length;
  const pct = (n: number) => (properties.length ? `${Math.round((n / properties.length) * 100)}%` : '0%');
  const allExpanded = sorted.length > 0 && sorted.every((p) => expandedIds.has(p.id));

  const headerCell = (label: string, key: SortKey, colIndex?: number) => (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--tx-muted)',
          fontFamily: font,
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
        }}
        onClick={() => toggleSort(key)}>
        {label}
        <SortCaret active={sortKey === key} dir={sortDir} />
      </div>
      {colIndex !== undefined && <ResizeHandle onStart={() => startResize(colIndex)} />}
    </div>
  );

  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ background: C.card, borderRadius: 16, padding: 24, fontFamily: font }}>
          <span style={{ color: L.critical, fontSize: 14 }}>
            Error loading properties: {error.message}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-properties-tab"
      style={{
        height: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '20px 0',
        scrollbarWidth: 'none',
      }}>
      {/* ─── Stat cards: the app's tile, value first, label beneath, a share strip and a caption ─── */}
      <StatRow columns={4}>
        <StatTile
          loading={isLoading}
          value={properties.length}
          label="Total homes"
          visual={
            <ShareStrip
              parts={[
                { label: 'With reports', color: L.resolved, value: withReports },
                { label: 'No reports', color: L.monitor, value: withoutReports },
              ]}
            />
          }
          caption="All homes"
        />
        <StatTile
          loading={isLoading}
          value={withReports}
          label="With reports"
          visual={
            <ShareStrip total={properties.length} parts={[{ label: 'With reports', color: L.resolved, value: withReports }]} />
          }
          caption={`${pct(withReports)} of homes`}
        />
        <StatTile
          loading={isLoading}
          value={withoutReports}
          label="No reports"
          visual={
            <ShareStrip total={properties.length} parts={[{ label: 'No reports', color: L.monitor, value: withoutReports }]} />
          }
          caption={`${pct(withoutReports)} of homes`}
        />
        <StatTile
          loading={isLoading}
          value={mappedCount}
          label="Mapped"
          visual={<ShareStrip total={properties.length} parts={[{ label: 'Mapped', color: L.low, value: mappedCount }]} />}
          caption={`${pct(mappedCount)} have coordinates`}
        />
      </StatRow>

      {/* ─── Toolbar ──────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <TextInput
          icon="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search address, city, ZIP, or owner…"
          sx={{ flex: '0 1 320px' }}
        />

        <Segmented
          label="View"
          value={viewMode}
          onChange={setViewMode}
          options={(
            [
              ['list', 'list', 'List'],
              ['map', 'map', 'Map'],
            ] as const
          ).map(([key, icon, label]) => [
            key,
            <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={icon} size={14} />
              {label}
            </span>,
          ])}
        />

        <div style={{ flex: 1 }} />

        <Button
          variant="add"
          onClick={(e) => {
            setAddAnchor(e.currentTarget);
            setIsAddPropertyOpen(true);
          }}>
          Add property
        </Button>

        <IconButton
          icon={allExpanded ? 'shrink' : 'expand'}
          label={allExpanded ? 'Collapse all' : 'Expand all'}
          onClick={() => setExpandedIds(allExpanded ? new Set() : new Set(sorted.map((p) => p.id)))}
        />
      </div>

      {/* ─── Bulk action bar ──────────────────────────────── */}
      {selectedIds.size > 0 && viewMode === 'list' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            padding: '10px 16px',
            borderRadius: '12px',
            background: 'rgba(var(--tx-ink-rgb), 0.05)',
          }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: font }}>
            {selectedIds.size} selected
          </span>
          <div style={{ width: 1, height: 20, background: C.border }} />
          <Button size="sm" variant="danger" icon="trash" onClick={handleBulkDelete}>
            Delete ({selectedIds.size})
          </Button>
          <div style={{ flex: 1 }} />
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      )}

      {/* ─── Content ──────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'map' ? (
          <div
            style={{
              background: C.card,
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
              borderRadius: '24px',
              overflow: 'hidden',
              flex: 1,
            }}>
            <PropertyMap properties={filtered} searchQuery={searchQuery} />
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* ─── Table ──────────────────────────────────────── */}
            <div
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '24px',
                overflow: 'hidden',
                paddingBottom: 5,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* Header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: grid,
                  gap: 20,
                  padding: '19px 20px 14px',
                  alignItems: 'center',
                  borderBottom: `1px solid ${C.borderLight}`,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </div>
                <div />
                {headerCell('Address', 'address', 0)}
                {headerCell('City', 'city', 1)}
                {headerCell('ST', 'state', 2)}
                {headerCell('ZIP', 'zip', 3)}
                {headerCell('Created By', 'owner', 4)}
                {headerCell('Updated', 'updated', 5)}
                {headerCell('Docs', 'docs', 6)}
                <div />
              </div>

              {/* Rows */}
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
                {isSearchPending ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: C.subtle, fontFamily: font }}>Loading…</div>
                  </div>
                ) : sorted.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: C.subtle, fontFamily: font }}>
                      {searchQuery ? 'No properties match your search' : 'No properties yet'}
                    </div>
                  </div>
                ) : (
                  sorted.map((property, idx) => {
                    const isExpanded = expandedIds.has(property.id);

                    const isSelected = selectedIds.has(property.id);

                    const isLast = idx === sorted.length - 1;

                    return (
                      <div
                        key={property.id}
                        style={
                          // an open row and its detail are one raised box, the app's open-row treatment (Advisor-Client's
                          // open client): inset 8px from the table, 16px corners, glass card, top light, small drop. Its
                          // contents pull in by the same 8px so they stay on the header's columns
                          isExpanded
                            ? {
                                position: 'relative',
                                zIndex: 1,
                                margin: '10px 8px',
                                borderRadius: 16,
                                overflow: 'hidden',
                                background: 'var(--tx-glass-card)',
                                backgroundImage: 'var(--tx-surface-light)',
                                boxShadow: 'var(--tx-shadow-sm)',
                              }
                            : undefined
                        }>
                        <div
                          onClick={(e) => {
                            // the hover tint goes as the row opens into its box
                            e.currentTarget.style.background = isSelected ? 'var(--tx-fill)' : 'transparent';
                            setExpandedIds((prev) => {
                              const next = new Set(prev);
                              if (isExpanded) next.delete(property.id);
                              else next.add(property.id);
                              return next;
                            });
                          }}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: grid,
                            gap: 20,
                            padding: isExpanded ? '12px 12px' : '12px 20px',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            background: isSelected ? 'var(--tx-fill)' : 'transparent',
                            borderBottom:
                              isExpanded || isLast ? 'none' : `1px solid ${C.borderLight}`,
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded && !isSelected)
                              e.currentTarget.style.background = 'var(--tx-fill)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isSelected ? 'var(--tx-fill)' : 'transparent';
                          }}>
                          {/* Checkbox */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleSelect(property.id)}
                            />
                          </div>

                          {/* Chevron */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <svg
                              style={{
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                color: C.subtle,
                              }}
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </div>

                          {/* Address */}
                          <div
                            style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {property.latitude && property.longitude && (
                              <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={C.lime}
                                strokeWidth="2"
                                strokeLinecap="round"
                                style={{ flexShrink: 0, cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMapTarget(property);
                                }}>
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                <circle cx="12" cy="10" r="3" />
                              </svg>
                            )}
                            <div
                              onClick={(e) => {
                                if (property.latitude && property.longitude) {
                                  e.stopPropagation();
                                  setMapTarget(property);
                                }
                              }}
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: C.text,
                                fontFamily: font,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor:
                                  property.latitude && property.longitude ? 'pointer' : 'default',
                              }}>
                              {property.address}
                            </div>
                          </div>

                          {/* City */}
                          <div
                            style={{
                              fontSize: 12,
                              color: C.muted,
                              fontFamily: font,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}>
                            {property.city}
                          </div>

                          {/* State */}
                          <div style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                            {property.state}
                          </div>

                          {/* ZIP */}
                          <div style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                            {property.zipCode}
                          </div>

                          {/* Created By — advisor / admin who initiated the
                              property add. Distinct from Owner (homeowner
                              relation). Legacy rows render "—". */}
                          <div style={{ minWidth: 0 }}>
                            <PersonCell person={property.creator} emptyLabel="—" />
                          </div>

                          {/* Updated */}
                          <div style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                            {relativeTime(property.updatedAt)}
                          </div>

                          {/* Docs */}
                          <div>
                            <ReportBadge count={property.inspectionReportCount} />
                          </div>

                          {/* Context menu */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <RowMenu
                              property={property}
                              onExpand={() =>
                                setExpandedIds((prev) => {
                                  const next = new Set(prev);
                                  if (isExpanded) next.delete(property.id);
                                  else next.add(property.id);
                                  return next;
                                })
                              }
                              onTransfer={() => setTransferTarget(property)}
                              onDelete={() => setDeleteTarget(property)}
                            />
                          </div>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <ExpandedRow
                            property={property}
                            onUpload={setUploadTarget}
                            onMap={() => setMapTarget(property)}
                          />
                        )}
                      </div>
                    );
                  })
                )}
                {hasNextPropertiesPage && (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <button
                      type="button"
                      onClick={() => fetchNextPropertiesPage()}
                      disabled={isFetchingNextPropertiesPage}
                      style={{
                        background: 'var(--tx-fill)',
                        border: 0,
                        borderRadius: 6,
                        padding: '8px 16px',
                        color: C.text,
                        fontFamily: font,
                        fontSize: 13,
                        cursor: isFetchingNextPropertiesPage ? 'default' : 'pointer',
                        opacity: isFetchingNextPropertiesPage ? 0.6 : 1,
                      }}>
                      {isFetchingNextPropertiesPage ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modals ────────────────────────────────────────── */}
      {transferTarget && (
        <TransferModal
          property={transferTarget}
          onClose={() => setTransferTarget(null)}
          onTransfer={handleTransfer}
          isPending={transferMutation.isPending}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          property={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleDelete}
          isPending={deleteMutation.isPending}
        />
      )}

      {mapTarget && <MapPopup property={mapTarget} onClose={() => setMapTarget(null)} />}

      <AddPropertyModal open={isAddPropertyOpen} anchorEl={addAnchor} onClose={() => setIsAddPropertyOpen(false)} />

      {uploadTarget && (
        <UploadModal
          property={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onUpload={(propertyId, file) => handleUpload(propertyId, file, 'seller')}
        />
      )}
    </div>
  );
};
